import React, { useEffect, useState, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Container,
  Alert,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import IndexCard from "../../../shared/IndexCard";
import PaginatedListLayout from "../../../../shared/pagination/PaginatedListLayout";

const JobListPage = () => {
  const [tabIndex, setTabIndex] = useState(0); // 0: 募集中, 1: 募集終了
  const [currentPage, setCurrentPage] = useState(1);
  const [jobs, setJobs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation(); // 画面遷移を検知するため
  const navigate = useNavigate();
  const flashMessage = location.state?.flashMessage; //　メッセージ受けとる

  const fetchJobs = useCallback(async (page = 1, nextTabIndex = 0) => {
    setLoading(true);
    const status = nextTabIndex === 0 ? "open" : "closed";

    try {
      const res = await fetch(
        `/company/jobs.json?status=${status}&page=${page}`,
      );
      const data = await res.json();

      setJobs(data.jobs ?? []);
      setPagination(data.pagination ?? null);

      // サーバー側でページ番号が丸められた場合に備えて、レスポンスに合わせて currentPage を同期
      if (data.pagination && typeof data.pagination.current_page === "number") {
        const serverPage = data.pagination.current_page;

        // 今回要求したページとサーバー返却ページが違うときだけ同期
        if (serverPage !== page) {
          setCurrentPage(serverPage);
        }
      }
    } catch (error) {
      console.error("求人取得に失敗しました", error);
      setJobs([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs(currentPage, tabIndex);
  }, [tabIndex, location.key, fetchJobs]);

  // ページ遷移やタブ切り替えのたびにスクロール位置を最上部にリセット
  const scrollToListTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, []);

  // タブ切り替え時の処理
  const handleTabChange = (_, newTabIndex) => {
    setTabIndex(newTabIndex);
    setCurrentPage(1);

    scrollToListTop();
  };

  // ページ切り替え時の処理
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchJobs(page, tabIndex);
    scrollToListTop();
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 2,
        mb: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* フラッシュメッセージ (固定) */}
      {flashMessage && (
        <Box sx={{ flexShrink: 0 }}>
          <Alert severity="success">
            {typeof flashMessage === "object"
              ? flashMessage.message
              : flashMessage}
          </Alert>
        </Box>
      )}

      {/* 固定ヘッダー領域 (Tabsまで) */}
      <Box
        sx={{
          position: "sticky",
          top: "63px",
          zIndex: 20,
          backgroundColor: "#F4F7FA",
          boxShadow: 0,
          mx: -2,
          px: 2,
          pt: 1.5,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h5" fontWeight="bold">
            求人
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/company/jobs/new")}
            sx={{ fontWeight: "bold" }}
          >
            ＋ 新規投稿
          </Button>
        </Box>

        <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="募集中" />
          <Tab label="募集終了" />
        </Tabs>
      </Box>

      {/* 一覧エリア */}
      <Box
        sx={{
          flexGrow: 1,
          pr: 1,
          pb: 10,
        }}
      >
        <PaginatedListLayout
          items={jobs}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          emptyText="現在、投稿された求人はありません。"
        >
          {jobs.map((job) => (
            <IndexCard
              key={job.id}
              item={job}
              type="job"
              component={Link}
              to={`/company/jobs/${job.id}`}
            />
          ))}
        </PaginatedListLayout>
      </Box>
    </Container>
  );
};

export default JobListPage;
